// Firebase Firestore service for task management
import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { canEdit } from './calendarStorage';

const TASKS_COLLECTION = 'tasks';

// Get all tasks for a specific user (optionally filtered by calendar)
export const getTasks = async (userId, calendarId = null) => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    try {
        const tasksRef = collection(db, TASKS_COLLECTION);

        // Build query based on whether calendarId is provided
        // Note: Removed orderBy to avoid needing composite indexes
        // We'll sort in memory instead
        let q;
        if (calendarId) {
            // Get tasks for specific calendar
            q = query(
                tasksRef,
                where('calendarId', '==', calendarId)
            );
        } else {
            // Get all tasks for user (backward compatibility)
            q = query(
                tasksRef,
                where('userId', '==', userId)
            );
        }

        const querySnapshot = await getDocs(q);
        const tasks = [];

        querySnapshot.forEach((doc) => {
            tasks.push({
                id: doc.id,
                ...doc.data(),
                // Convert Firestore Timestamps to ISO strings
                createdAt: doc.data().createdAt?.toDate().toISOString(),
                updatedAt: doc.data().updatedAt?.toDate().toISOString()
            });
        });

        // Sort by createdAt in memory (newest first)
        tasks.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        return tasks;
    } catch (error) {
        console.error('Error getting tasks from Firestore:', error);
        throw error;
    }
};

// Get a single task by ID
export const getTaskById = async (taskId, userId) => {
    if (!userId || !taskId) {
        throw new Error('User ID and Task ID are required');
    }

    try {
        const taskRef = doc(db, TASKS_COLLECTION, taskId);
        const taskDoc = await getDoc(taskRef);

        if (!taskDoc.exists()) {
            throw new Error('Task not found');
        }

        const taskData = taskDoc.data();

        // Verify the task belongs to the user
        if (taskData.userId !== userId) {
            throw new Error('Unauthorized access to task');
        }

        return {
            id: taskDoc.id,
            ...taskData,
            createdAt: taskData.createdAt?.toDate().toISOString(),
            updatedAt: taskData.updatedAt?.toDate().toISOString()
        };
    } catch (error) {
        console.error('Error getting task by ID:', error);
        throw error;
    }
};

// Get tasks by date for a specific user
export const getTasksByDate = async (date, userId) => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    try {
        const tasksRef = collection(db, TASKS_COLLECTION);
        const q = query(
            tasksRef,
            where('userId', '==', userId),
            where('dueDate', '==', date)
        );

        const querySnapshot = await getDocs(q);
        const tasks = [];

        querySnapshot.forEach((doc) => {
            tasks.push({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate().toISOString(),
                updatedAt: doc.data().updatedAt?.toDate().toISOString()
            });
        });

        return tasks;
    } catch (error) {
        console.error('Error getting tasks by date:', error);
        throw error;
    }
};

// Create a new task
export const createTask = async (taskData, userId, calendarId) => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    if (!calendarId) {
        throw new Error('Calendar ID is required');
    }

    try {
        const tasksRef = collection(db, TASKS_COLLECTION);

        const newTask = {
            userId: userId,
            calendarId: calendarId,
            task: taskData.task,
            owner: taskData.owner, // Now an object with id and name
            dueDate: taskData.dueDate,
            acceptanceCriteria: taskData.acceptanceCriteria || '',
            dependencies: taskData.dependencies || '',
            contingencyPlan: taskData.contingencyPlan || '',
            priority: taskData.priority || 'medium',
            status: taskData.status || 'not-started',
            completed: taskData.completed || false,
            completedAt: taskData.completedAt || null,
            goalId: taskData.goalId && taskData.goalId !== '' ? taskData.goalId : null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };

        const docRef = await addDoc(tasksRef, newTask);

        // Return the created task with its ID
        return {
            id: docRef.id,
            ...newTask,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('Error creating task:', error);
        throw error;
    }
};

// Update a task
export const updateTask = async (taskId, updates, userId) => {
    if (!userId || !taskId) {
        throw new Error('User ID and Task ID are required');
    }

    try {
        const taskRef = doc(db, TASKS_COLLECTION, taskId);

        // First verify the task exists and belongs to the user
        const taskDoc = await getDoc(taskRef);
        if (!taskDoc.exists()) {
            throw new Error('Task not found');
        }

        if (taskDoc.data().userId !== userId) {
            throw new Error('Unauthorized access to task');
        }

        const updateData = {
            ...updates,
            updatedAt: serverTimestamp()
        };

        // Ensure goalId is null if it's an empty string
        if ('goalId' in updateData && updateData.goalId === '') {
            updateData.goalId = null;
        }

        await updateDoc(taskRef, updateData);

        // Return the updated task
        return {
            id: taskId,
            ...taskDoc.data(),
            ...updates,
            updatedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('Error updating task:', error);
        throw error;
    }
};

// Delete a task
export const deleteTask = async (taskId, userId) => {
    if (!userId || !taskId) {
        throw new Error('User ID and Task ID are required');
    }

    try {
        const taskRef = doc(db, TASKS_COLLECTION, taskId);

        // First verify the task exists and belongs to the user
        const taskDoc = await getDoc(taskRef);
        if (!taskDoc.exists()) {
            throw new Error('Task not found');
        }

        if (taskDoc.data().userId !== userId) {
            throw new Error('Unauthorized access to task');
        }

        await deleteDoc(taskRef);
        return true;
    } catch (error) {
        console.error('Error deleting task:', error);
        throw error;
    }
};


// Export tasks as JSON for a user
export const exportTasks = async (userId) => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    try {
        const tasks = await getTasks(userId);
        const dataStr = JSON.stringify(tasks, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `tasks-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error exporting tasks:', error);
        throw error;
    }
};

// Import tasks from JSON for a user
export const importTasks = async (jsonData, userId, calendarId) => {
    if (!userId || !calendarId) {
        throw new Error('User ID and Calendar ID are required');
    }

    try {
        const tasks = JSON.parse(jsonData);
        if (!Array.isArray(tasks)) {
            throw new Error('Invalid data format');
        }

        const tasksRef = collection(db, TASKS_COLLECTION);

        const importPromises = tasks.map(task => {
            const { id, createdAt, updatedAt, ...taskData } = task;
            return addDoc(tasksRef, {
                ...taskData,
                userId: userId,
                calendarId: calendarId,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
        });

        await Promise.all(importPromises);
        return true;
    } catch (error) {
        console.error('Error importing tasks:', error);
        throw error;
    }
};

// Get storage statistics for a user
export const getStorageStats = async (userId) => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    try {
        const tasks = await getTasks(userId);
        const storageSize = new Blob([JSON.stringify(tasks)]).size;

        return {
            totalTasks: tasks.length,
            storageSize: storageSize,
            storageSizeKB: (storageSize / 1024).toFixed(2)
        };
    } catch (error) {
        console.error('Error getting storage stats:', error);
        throw error;
    }
};

/**
 * Get tasks associated with a specific goal
 * @param {string} goalId - The goal ID
 * @returns {Promise<Array>} Array of task objects
 */
export const getTasksByGoal = async (goalId, calendarId = null) => {
    if (!goalId) {
        throw new Error('Goal ID is required');
    }

    try {
        const tasksRef = collection(db, TASKS_COLLECTION);
        let q;

        if (calendarId) {
            // Include calendarId for better permissions and performance
            q = query(
                tasksRef,
                where('calendarId', '==', calendarId),
                where('goalId', '==', goalId)
            );
        } else {
            // Fallback to goalId only (may fail with permissions)
            q = query(
                tasksRef,
                where('goalId', '==', goalId)
            );
        }

        const querySnapshot = await getDocs(q);
        const tasks = [];

        querySnapshot.forEach((doc) => {
            tasks.push({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate().toISOString(),
                updatedAt: doc.data().updatedAt?.toDate().toISOString()
            });
        });

        // Sort by dueDate
        tasks.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

        return tasks;
    } catch (error) {
        console.error('Error getting tasks by goal:', error);
        throw error;
    }
};

/**
 * Update task's goal association
 * @param {string} taskId - The task ID
 * @param {string|null} goalId - The goal ID to associate with, or null to disassociate
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} Updated task object
 */
export const updateTaskGoalAssociation = async (taskId, goalId, userId) => {
    if (!userId || !taskId) {
        throw new Error('User ID and Task ID are required');
    }

    try {
        const taskRef = doc(db, TASKS_COLLECTION, taskId);

        // Verify the task exists
        const taskDoc = await getDoc(taskRef);
        if (!taskDoc.exists()) {
            throw new Error('Task not found');
        }

        const taskData = taskDoc.data();

        // Check if user has access to the task
        if (taskData.userId !== userId) {
            throw new Error('Unauthorized access to task');
        }

        // If associating with a goal, verify user has edit permission on the calendar
        if (goalId && taskData.calendarId) {
            const hasPermission = await canEdit(taskData.calendarId, userId);
            if (!hasPermission) {
                throw new Error('Insufficient permissions to associate task with goal');
            }
        }

        await updateDoc(taskRef, {
            goalId: goalId,
            updatedAt: serverTimestamp()
        });

        return {
            id: taskId,
            ...taskData,
            goalId: goalId,
            updatedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('Error updating task goal association:', error);
        throw error;
    }
};
